export type UserRole = "superadmin" | "admin" | "student";

export interface User {
  id: string;
  email: string;
  nome?: string;
  cognome?: string;
  telefono?: string | null;
  role: UserRole;
  active: boolean;
  tags?: Tag[];
}

export interface Tag {
  id: string;
  nome: string;
  colore: string;
  nContatti?: number;
}

// Risultato dell'endpoint /admin/tests/:id/send-credentials
export interface SendCredentialsResult {
  destinatari: number;
  perCanale: {
    email?: { tentati: number; inviati: number; falliti: number };
    whatsapp?: { tentati: number; inviati: number; falliti: number; motivo?: string };
    manual?: Array<{ contactId: string; email: string; nome: string; telefono: string | null; url: string }>;
  };
  errori: string[];
  publicUrl?: string;
}

export interface RegistrationCampaign {
  id: string;
  nome: string;
  slug: string;
  fonte: string;
  attiva: boolean;
  richiedeApprovazione: boolean;
  createdAt: string;
  closedAt: string | null;
  nIscritti: number;
  nPending: number;
}

export interface WhatsAppStatus {
  state: "disconnected" | "connecting" | "qr" | "connected" | "logged_out";
  jid: string | null;
  lastQr: string | null;
  lastQrAt: string | null;
  lastError: string | null;
  connectedAt: string | null;
}

// Helper: è un docente (admin o superadmin)?
export const isDocente = (role: UserRole) =>
  role === "admin" || role === "superadmin";

export interface Test {
  id: string;
  titolo: string;
  descrizione?: string;
  slug: string;
  questioniPerStudente?: number;
  durataMinuti?: number;
  pubblicoAnonimo: boolean;
  status: "bozza" | "in_revisione" | "pubblicato" | "chiuso";
  createdAt: string;
  publishedAt?: string;
  _count?: { questions: number; submissions: number };
}

export interface Question {
  id: string;
  testId: string;
  testo: string;
  tipo: "chiusa" | "aperta" | "scala";
  opzioni?: string[];
  rispostaCorretta?: number;
  spiegazione?: string;
  sezione?: string;
  coloreSezione?: string;
  generataDaAi: boolean;
  approvata: boolean;
  ordine: number;
}

// Domanda per lo studente — rispostaCorretta MAI inclusa
export interface QuestionForStudent {
  id: string;
  testo: string;
  tipo: "chiusa" | "aperta" | "scala";
  opzioniShufflate?: string[];
  sezione?: string;
  coloreSezione?: string;
  spiegazione?: string; // solo dopo che lo studente ha risposto
}

// Stato completo quiz restituito dal server
export interface QuizState {
  submissionId: string;
  totaleDomande: number;
  prossimaIndex: number;
  secondiRimasti?: number; // calcolato dal server, non dal client
  domandaCorrente: QuestionForStudent;
}

export interface AnswerResponse {
  isCorrect?: boolean;
  spiegazione?: string;
  secondiRimasti?: number;
}

export interface Submission {
  id: string;
  testId: string;
  nomeAnonimo?: string;
  cognomeAnonimo?: string;
  nomeProgetto?: string;
  ultimaDomandaIndex: number;
  startedAt: string;
  completedAt?: string;
  scadeAt?: string;
  durataSecondi?: number;
  scoreTotale?: number;
  scoreMassimo?: number;
  percentuale?: number;
  livello?: string;
  sezioniScore?: Record<string, { score: number; total: number }>;
}

export interface AiInterpretation {
  comprensione: 1 | 2 | 3 | 4 | 5;
  puntiForti: string[];
  lacune: string[];
  suggerimento: string;
  sintesi: string; // unico campo visibile allo studente
}

export interface AiGeneratedQuestion {
  testo: string;
  tipo: "chiusa" | "aperta";
  opzioni?: string[];
  rispostaCorretta?: number;
  spiegazione: string;
  sezione: string;
}

export interface BulkImportResult {
  importate: number;
  duplicate: string[];
  invalide: string[];
  errori: string[];
}

export interface HealthCheck {
  api: "ok" | "error";
  database: "ok" | "error";
  redis: "ok" | "error";
}

export const LEVELS = [
  { min: 0,  max: 59,  label: "Insufficiente", emoji: "📉",
    color: "#E8613C", description: "Sono necessarie ulteriori competenze da acquisire." },
  { min: 60, max: 74,  label: "Sufficiente",   emoji: "📘",
    color: "#2C5F8A", description: "Hai le basi. Il corso rafforzerà le aree più deboli." },
  { min: 75, max: 89,  label: "Buono",         emoji: "🚀",
    color: "#5B3E8A", description: "Buona preparazione. Potrai approfondire i temi avanzati." },
  { min: 90, max: 100, label: "Ottimo",        emoji: "⭐",
    color: "#2A7A4B", description: "Preparazione eccellente. Sarai un punto di riferimento." },
] as const;

export function getLevel(score: number, max: number) {
  if (max === 0) return LEVELS[0];
  const pct = Math.round((score / max) * 100);
  return LEVELS.find(l => pct >= l.min && pct <= l.max) ?? LEVELS[0];
}
